import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RestaurantKitchenOrderService from '../../services/restaurant/RestaurantKitchenOrderService';
import RestaurantPreparationStationService from '../../services/restaurant/RestaurantPreparationStationService';
import { AppDispatch, RootState } from '../../store/store';
import {
  replaceKitchenOrder,
  resetKitchenBoard,
  setKitchenActionKey,
  setKitchenFilter,
  setKitchenLoading,
  setKitchenOrders,
  setKitchenRefreshing,
  setKitchenStations,
} from '../../store/slices/restaurant/restaurantKitchenOrderSlice';
import {
  RestaurantKitchenOrderFilters,
  RestaurantKitchenPrintJobDTO,
  RestaurantKitchenStationPrintState,
} from '../../types/restaurant/RestaurantKitchenOrderTypes';
import useApiErrorHandler from '../useApiErrorHandler';
import useAlerts from '../useAlerts';
import { processKitchenPrintJobs } from '../../services/printing/kitchen-print.service';

const useRestaurantKitchenOrders = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantKitchenOrder);
  const { handle } = useApiErrorHandler();
  const { show } = useAlerts();
  const loadRequestRef = useRef(0);
  const reprintRequestsRef = useRef(new Set<number>());
  const [printStates, setPrintStates] = useState<
    Record<number, RestaurantKitchenStationPrintState[]>
  >({});

  const updatePrintState = (
    kitchenOrderId: number,
    job: RestaurantKitchenPrintJobDTO,
    status: RestaurantKitchenStationPrintState['status'],
    error?: unknown
  ) => {
    setPrintStates((current) => {
      const previous = current[kitchenOrderId] ?? [];
      const next: RestaurantKitchenStationPrintState = {
        jobId: job.jobId,
        preparationStationId: job.preparationStationId,
        preparationStationName: job.preparationStationName,
        status,
        ...(error
          ? {
              error:
                (error as Error)?.message ||
                'No se pudo imprimir la comanda de esta estación.',
            }
          : {}),
      };
      const index = previous.findIndex((item) => item.jobId === job.jobId);
      const stationStates = [...previous];
      if (index >= 0) stationStates[index] = next;
      else stationStates.push(next);
      return { ...current, [kitchenOrderId]: stationStates };
    });
  };

  const load = async (silent = false) => {
    if (!branchId) return;
    const requestId = ++loadRequestRef.current;
    if (silent) {
      dispatch(setKitchenLoading(false));
      dispatch(setKitchenRefreshing(true));
    } else {
      dispatch(setKitchenRefreshing(false));
      dispatch(setKitchenLoading(true));
    }
    try {
      const response = await RestaurantKitchenOrderService.list(
        branchId,
        state.filters
      );
      if (requestId === loadRequestRef.current) {
        dispatch(setKitchenOrders(response.data ?? []));
      }
    } catch (error) {
      if (!silent) handle(error);
    } finally {
      if (requestId === loadRequestRef.current) {
        dispatch(
          silent ? setKitchenRefreshing(false) : setKitchenLoading(false)
        );
      }
    }
  };

  const loadStations = async () => {
    if (!branchId) return;
    try {
      const response = await RestaurantPreparationStationService.list(
        branchId,
        { page: 0, size: 200, active: true, enabled: true }
      );
      dispatch(setKitchenStations(response.content));
    } catch (error) {
      handle(error);
    }
  };

  const runAction = async (
    key: string,
    action: () => ReturnType<typeof RestaurantKitchenOrderService.start>
  ) => {
    if (state.actionKey) return;
    dispatch(setKitchenActionKey(key));
    try {
      const response = await action();
      dispatch(replaceKitchenOrder(response.data));
      await load(true);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setKitchenActionKey(null));
    }
  };

  const updateFilter = <K extends keyof RestaurantKitchenOrderFilters>(
    field: K,
    value: RestaurantKitchenOrderFilters[K]
  ) => dispatch(setKitchenFilter({ field, value }));

  const reprint = async (kitchenOrderId: number) => {
    if (state.actionKey || reprintRequestsRef.current.has(kitchenOrderId))
      return;
    reprintRequestsRef.current.add(kitchenOrderId);
    dispatch(setKitchenActionKey(`order-${kitchenOrderId}-reprint`));

    let jobs: RestaurantKitchenPrintJobDTO[];
    try {
      jobs = await RestaurantKitchenOrderService.reprint(kitchenOrderId);
      setPrintStates((current) => ({ ...current, [kitchenOrderId]: [] }));
    } catch (error) {
      handle(error);
      return;
    } finally {
      reprintRequestsRef.current.delete(kitchenOrderId);
      dispatch(setKitchenActionKey(null));
    }

    try {
      const results = await processKitchenPrintJobs(
        jobs,
        undefined,
        (job, status, error) =>
          updatePrintState(kitchenOrderId, job, status, error)
      );

      results.forEach((result) => {
        if (result.success) return;
        show(
          'error',
          `Error de impresión · ${result.job.preparationStationName}`,
          (result.error as Error)?.message ||
            'No se pudo imprimir la comanda de esta estación.'
        );
      });

      if (results.some((result) => result.success)) {
        show(
          'success',
          'Reimpresión solicitada',
          'Los trabajos disponibles fueron enviados a sus impresoras.'
        );
      }
    } catch (error) {
      handle(error);
    }
  };

  return {
    ...state,
    printStates,
    load,
    loadStations,
    updateFilter,
    start: (id: number) =>
      runAction(`order-${id}-start`, () =>
        RestaurantKitchenOrderService.start(id)
      ),
    markReady: (id: number) =>
      runAction(`order-${id}-ready`, () =>
        RestaurantKitchenOrderService.markReady(id)
      ),
    startItem: (orderId: number, itemId: number) =>
      runAction(`item-${itemId}-start`, () =>
        RestaurantKitchenOrderService.startItem(orderId, itemId)
      ),
    markItemReady: (orderId: number, itemId: number) =>
      runAction(`item-${itemId}-ready`, () =>
        RestaurantKitchenOrderService.markItemReady(orderId, itemId)
      ),
    reprint,
    reset: () => {
      dispatch(resetKitchenBoard());
    },
  };
};

export default useRestaurantKitchenOrders;
